import React, { useState } from "react";
import {
  Card,
  Button,
  Modal,
  Image,
  Tag,
  Space,
  Typography,
  Divider,
  Row,
  Col,
  Progress,
} from "antd";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  EyeOutlined,
  DollarOutlined,
  UserOutlined,
} from "@ant-design/icons";
import styled from "styled-components";

const { Text, Title } = Typography;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;

  @media (min-width: 992px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

const MetricCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  padding: 0.875rem 1rem;
  background: ${(props) => props.$background || "#fafafa"};
  border: 1px solid ${(props) => props.$border || "#f0f0f0"};
  border-radius: 8px;
  min-width: 0;
`;

const MetricLabel = styled(Text)`
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.45);
  line-height: 1.3;
`;

const MetricValue = styled.div`
  font-size: clamp(1rem, 2.5vw, 1.375rem);
  font-weight: 600;
  line-height: 1.25;
  color: ${(props) => props.$color || "rgba(0, 0, 0, 0.88)"};
  word-break: break-word;
`;

const StatusRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 1rem;
  padding-top: 0.75rem;
  border-top: 1px solid #f0f0f0;
`;

const ProgressBlock = styled.div`
  margin-top: 1rem;
`;

const ActionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const HintBlock = styled.div`
  margin-top: 0.75rem;
`;

const PaymentProofViewer = ({ quote }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);

  // Exclude admin verification POPs — those must not look like customer payment progress
  const customerProofs = (quote?.payment?.proofs || []).filter(
    (p) => p?.via !== "admin"
  );
  const customerProofFiles = (
    quote?.payment?.proofData?.proofFiles || []
  ).filter((f) => f?.source !== "admin_upload");
  const hasPaymentProof =
    quote?.payment?.proofReceived ||
    customerProofs.length > 0 ||
    customerProofFiles.length > 0 ||
    !!quote?.payment?.proofImage?.url;
  const isPendingPayment = quote?.status === "pending_payment";
  const isDepositPaid = quote?.payment?.depositPaid;
  const formatCurrency = (v) =>
    new Intl.NumberFormat("en-ZA", {
      style: "currency",
      currency: "ZAR",
      maximumFractionDigits: 0,
    }).format(Number(v || 0));
  const totalPaid = Number(
    quote?.payments?.totalPaid || quote?.payment?.proofData?.amount || 0
  );
  const totalAmount = Number(
    quote?.totalCost !== undefined
      ? quote.totalCost
      : (quote?.estimatedCost || 0) + (quote?.addOnsCost || 0)
  );
  const balance = Math.max(totalAmount - totalPaid, 0);
  // Required deposit: preserve quote-specific overrides, including zero.
  const requiredAmount = Number(
    quote?.deposit?.requiredAmount ??
      (quote?.payment?.requiredAmountOverridden
        ? quote?.payment?.requiredAmount
        : undefined) ??
      quote?.deposit?.minimumAmount ??
      0
  );
  const paidPercent =
    totalAmount > 0
      ? Math.min(100, Math.round((totalPaid / totalAmount) * 100))
      : 0;

  const showPaymentProof = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not set";
    return new Date(dateString).toLocaleDateString("en-ZA", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusColor = () => {
    if (isDepositPaid) return "success";
    if (hasPaymentProof) return "processing";
    if (isPendingPayment) return "warning";
    return "default";
  };

  const getStatusText = () => {
    if (isDepositPaid) return "Deposit Paid";
    if (hasPaymentProof) return "Proof Received";
    if (isPendingPayment) return "Awaiting Payment";
    return "No Payment";
  };

  const getStatusIcon = () => {
    if (isDepositPaid) return <CheckCircleOutlined />;
    if (hasPaymentProof) return <ClockCircleOutlined />;
    if (isPendingPayment) return <ClockCircleOutlined />;
    return null;
  };

  return (
    <>
      <Card
        title={
          <Space>
            <DollarOutlined />
            <Text>Payment Status</Text>
          </Space>
        }
        size="small"
        style={{ marginBottom: 0 }}
      >
        <MetricsGrid>
          <MetricCard $background="#f0f7ff" $border="#d6e4ff">
            <MetricLabel>Total Amount</MetricLabel>
            <MetricValue $color="#1677ff">
              {formatCurrency(totalAmount)}
            </MetricValue>
          </MetricCard>
          <MetricCard $background="#fff7e6" $border="#ffd591">
            <MetricLabel>Deposit Required</MetricLabel>
            <MetricValue $color="#d46b08">
              {formatCurrency(requiredAmount)}
            </MetricValue>
          </MetricCard>
          <MetricCard
            $background={totalPaid > 0 ? "#f6ffed" : "#fafafa"}
            $border={totalPaid > 0 ? "#b7eb8f" : "#f0f0f0"}
          >
            <MetricLabel>Amount Paid</MetricLabel>
            <MetricValue $color={totalPaid > 0 ? "#389e0d" : "rgba(0,0,0,0.45)"}>
              {formatCurrency(totalPaid)}
            </MetricValue>
          </MetricCard>
          <MetricCard>
            <MetricLabel>Balance Remaining</MetricLabel>
            <MetricValue $color={balance > 0 ? "#cf1322" : "#389e0d"}>
              {formatCurrency(balance)}
            </MetricValue>
          </MetricCard>
        </MetricsGrid>

        <StatusRow>
          <Space wrap size={[8, 8]}>
            <Text strong>Status</Text>
            <Tag color={getStatusColor()} icon={getStatusIcon()}>
              {getStatusText()}
            </Tag>
          </Space>
          {totalAmount > 0 && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {paidPercent}% of total received
            </Text>
          )}
        </StatusRow>

        {totalAmount > 0 && (
          <ProgressBlock>
            <Progress
              percent={paidPercent}
              strokeColor={
                isDepositPaid
                  ? "#52c41a"
                  : hasPaymentProof
                    ? "#1677ff"
                    : "#faad14"
              }
              showInfo={false}
              size="small"
            />
          </ProgressBlock>
        )}

        {isPendingPayment && !hasPaymentProof && (
          <HintBlock>
            <Text type="secondary">
              Customer has not submitted payment proof yet.
            </Text>
          </HintBlock>
        )}

        {isPendingPayment && !isDepositPaid && (
          <HintBlock>
            <Text type="secondary">
              Use <Text strong>Verify Deposit (SOP #3)</Text> on the Verification
              tab to confirm the amount received and create the order and
              invoice.
            </Text>
          </HintBlock>
        )}

        {hasPaymentProof && (
          <ActionsRow>
            <Button
              type="primary"
              icon={<EyeOutlined />}
              onClick={showPaymentProof}
              size="small"
            >
              View Payment Proof
            </Button>
          </ActionsRow>
        )}
      </Card>

      <Modal
        title="Payment Proof Details"
        open={isModalVisible}
        onCancel={handleCancel}
        footer={[
          <Button key="back" onClick={handleCancel}>
            Close
          </Button>,
        ]}
        width={600}
        zIndex={3000}
        getContainer={() => document.body}
        maskClosable={true}
        destroyOnHidden
      >
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <Row gutter={16}>
            <Col span={12}>
              <Text strong>Quote ID:</Text>
              <br />
              <Text code>{quote?.id}</Text>
            </Col>
            <Col span={12}>
              <Text strong>Customer:</Text>
              <br />
              <Text>{quote?.customerName || "Not provided"}</Text>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Text strong>Phone:</Text>
              <br />
              <Text>{quote?.customerPhone || "Not provided"}</Text>
            </Col>
            <Col span={12}>
              <Text strong>Amount:</Text>
              <br />
              <Text>{formatCurrency(totalAmount)}</Text>
            </Col>
          </Row>

          <Divider style={{ margin: 0 }} />

          {hasPaymentProof && (
            <>
              <Title level={5}>Payment Proof</Title>
              <Row gutter={16}>
                <Col span={12}>
                  <Text strong>Received Via:</Text>
                  <br />
                  <Tag color="blue" icon={<UserOutlined />}>
                    {quote?.payment?.proofReceivedVia || "Unknown"}
                  </Tag>
                </Col>
                <Col span={12}>
                  <Text strong>Received At:</Text>
                  <br />
                  <Text>{formatDate(quote?.payment?.proofReceivedAt)}</Text>
                </Col>
              </Row>

              {(() => {
                const filesFromProofs = customerProofs
                  .flatMap((p) => (Array.isArray(p?.files) ? p.files : []))
                  .filter(Boolean);
                const filesFromProofData = customerProofFiles;
                const legacyFile = quote?.payment?.proofImage?.url
                  ? [
                      {
                        downloadURL: quote.payment.proofImage.url,
                        mime: "image/*",
                      },
                    ]
                  : [];
                const allFiles = [
                  ...filesFromProofs,
                  ...filesFromProofData,
                  ...legacyFile,
                ];

                if (!allFiles.length) return null;

                const isImage = (mimeOrUrl) => {
                  const m = (mimeOrUrl || "").toString().toLowerCase();
                  return (
                    m.includes("image/") ||
                    /\.(png|jpg|jpeg|gif|webp)$/i.test(mimeOrUrl)
                  );
                };
                const isPdf = (mimeOrUrl) => {
                  const m = (mimeOrUrl || "").toString().toLowerCase();
                  return (
                    m.includes("application/pdf") || /\.pdf$/i.test(mimeOrUrl)
                  );
                };

                return (
                  <Space direction="vertical" size={8} style={{ width: "100%" }}>
                    <Text strong>Files:</Text>
                    <Space wrap>
                      {allFiles.map((f, idx) => {
                        const url = f.downloadURL || f.previewUrl || f.url;
                        const label =
                          f.originalName || f.filename || `File ${idx + 1}`;
                        if (isImage(f.mime || url)) {
                          return (
                            <Space
                              key={idx}
                              direction="vertical"
                              align="center"
                              size={4}
                            >
                              <Image width={200} src={url} alt={label} />
                              <Button
                                type="link"
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Open Original
                              </Button>
                            </Space>
                          );
                        }
                        if (isPdf(f.mime || url)) {
                          return (
                            <Space
                              key={idx}
                              direction="vertical"
                              size={8}
                              style={{ width: 320 }}
                            >
                              <Text>{label}</Text>
                              <embed
                                src={url}
                                type="application/pdf"
                                width="100%"
                                height="280px"
                              />
                              <Button
                                type="link"
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Open Original
                              </Button>
                            </Space>
                          );
                        }
                        return (
                          <Button
                            key={idx}
                            type="default"
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {label}
                          </Button>
                        );
                      })}
                    </Space>
                  </Space>
                );
              })()}

              <Divider style={{ margin: 0 }} />

              <Title level={5}>Payment Details</Title>
              <Row gutter={16}>
                <Col span={12}>
                  <Text strong>Required Amount:</Text>
                  <br />
                  <Text>{formatCurrency(totalAmount)}</Text>
                </Col>
                <Col span={12}>
                  <Text strong>Deposit Required:</Text>
                  <br />
                  <Text>{formatCurrency(requiredAmount)}</Text>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Text strong>Paid:</Text>
                  <br />
                  <Text>{formatCurrency(totalPaid)}</Text>
                </Col>
                <Col span={12}>
                  <Text strong>Balance:</Text>
                  <br />
                  <Text>{formatCurrency(balance)}</Text>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Text strong>Deposit Paid:</Text>
                  <br />
                  <Tag color={isDepositPaid ? "success" : "default"}>
                    {isDepositPaid ? "Yes" : "No"}
                  </Tag>
                </Col>
                <Col span={12}>
                  <Text strong>Paid At:</Text>
                  <br />
                  <Text>{formatDate(quote?.payment?.depositPaidAt)}</Text>
                </Col>
              </Row>

              {isDepositPaid && (
                <Row gutter={16}>
                  <Col span={12}>
                    <Text strong>Paid By:</Text>
                    <br />
                    <Text>{quote?.payment?.depositPaidBy || "Unknown"}</Text>
                  </Col>
                  <Col span={12}>
                    <Text strong>Payment Reference:</Text>
                    <br />
                    <Text code>
                      {quote?.payment?.paymentReference || "Not provided"}
                    </Text>
                  </Col>
                </Row>
              )}
            </>
          )}

          {!hasPaymentProof && (
            <Space
              direction="vertical"
              align="center"
              style={{ width: "100%", padding: "20px 0" }}
            >
              <ClockCircleOutlined style={{ fontSize: 48, color: "#faad14" }} />
              <Text type="secondary">No payment proof received yet</Text>
            </Space>
          )}
        </Space>
      </Modal>
    </>
  );
};

export default PaymentProofViewer;
